<template>
    <div class="payment-container">
      <div class="payment-flow">

        <h1 class="main-title">Registro Informe Semanal Eurekers</h1>

        <!-- Login Prompt -->
        <div class="login-prompt-card">
          <p>¿Ya tienes una cuenta? <a href="#" @click.prevent="redirectToSubscribePage" class="login-link">Haz click aquí</a></p>
        </div>

        <!-- Progress Indicator -->
        <div class="progress-indicator">
          <div 
            v-for="stage in 3" 
            :key="stage" 
            :class="['circle', { active: currentStage === stage, completed: currentStage > stage }]">
            {{ stage }}
          </div>
        </div>
        
        <!-- Stage 1: Verificación de Teléfono y Cuenta IBAN -->
        <div v-if="currentStage === 1" class="stage stage-1">
          <h2>Verificación de Teléfono y Cuenta IBAN</h2>
          
          <div class="verification-group">
            <div class="phone-input-container">
              <input 
                type="tel" 
                v-model="formData.phone" 
                placeholder="Teléfono" 
                :disabled="codeSent && !changingPhone" 
                :class="{ 'input-disabled': codeSent && !changingPhone, 'input-active-step': currentStage === 1 && (!codeSent || changingPhone) && !validations.phoneVerified }"
              />
              <button 
                class="secondary-btn" 
                @click="sendPhoneCode" 
                :disabled="!formData.phone || loading.sendPhoneCode || (codeSent && !changingPhone)"
                v-if="!codeSent || changingPhone"
              >
                {{ loading.sendPhoneCode ? 'Enviando...' : 'Enviar código' }}
              </button>
              <button 
                class="text-btn change-phone-btn" 
                @click="enablePhoneChange" 
                v-if="codeSent && !changingPhone"
              >
                Cambiar teléfono
              </button>
            </div>
            
            <div v-if="codeSent && !changingPhone" class="code-verification-container">
              <input 
                type="text" 
                v-model="formData.phoneCode" 
                placeholder="Código de teléfono"
                @keyup.enter="validatePhoneCode"
                :disabled="validations.phoneVerified"
                :class="{ 'input-disabled': validations.phoneVerified, 'input-active-step': currentStage === 1 && codeSent && !changingPhone && !validations.phoneVerified }"
              />
              <button 
                class="secondary-btn" 
                @click="validatePhoneCode" 
                :disabled="!formData.phoneCode || loading.validatePhoneCode || validations.phoneVerified"
              >
                {{ loading.validatePhoneCode ? 'Verificando...' : validations.phoneVerified ? 'Verificado' : 'Verificar teléfono' }}
              </button>
              <span class="success-message" v-if="validations.phoneVerified">Teléfono verificado</span>
              <span class="info-message" v-if="infoMessages.phoneCode && !errors.phoneCode && !validations.phoneVerified">{{ infoMessages.phoneCode }}</span>
              <span class="error-message" v-if="errors.phoneCode && !validations.phoneVerified">{{ errors.phoneCode }}</span>
            </div>
          </div>

          <div class="form-group">
            <SpanishIbanInput
              v-model="formData.iban" 
              placeholder="IBAN (ES + 22 dígitos)"
              :disabled="!validations.phoneVerified"
              :class="{ 'input-disabled': !validations.phoneVerified, 'input-active-step': currentStage === 1 && validations.phoneVerified }"
            />
            <span class="error-message" v-if="errors.iban">{{ errors.iban }}</span>
            <div class="info-container">
              <p class="info-text">
                Estás a punto de suscribirte a nuestro informe semanal.<br>
                → El precio de la suscripción es de 10€/mes.<br>
                → La facturación es anual: 120€ + IVA (total de 145,20€ con IVA incluido).<br>
                → El pago se realiza de forma segura y automática cada 12 meses, salvo que canceles antes de la próxima renovación.
              </p>
            </div>
            <div class="terms-container">
              <div class="checkbox-container">
                <input type="checkbox" id="terms" v-model="formData.termsAccepted" />
                <label for="terms" class="terms-text">
                  Acepto estos términos y condiciones
                </label>
              </div>
            </div>
            <span class="error-message" v-if="errors.terms">{{ errors.terms }}</span>
          </div>

          <div class="navigation-buttons">
            <button 
              class="primary-btn"
              @click="nextStage"
              :disabled="!validations.phoneVerified || !formData.iban || formData.iban.length < 24"
            >Siguiente</button>
          </div>
        </div>
        
        <!-- Stage 2: Datos Personales -->
        <div v-if="currentStage === 2" class="stage stage-2">
          <h2>Datos Personales</h2>
          <form @submit.prevent="nextStage">
            <div class="form-group">
              <input type="text" v-model="formData.name" placeholder="Nombre" />
              <input type="text" v-model="formData.apellidos" placeholder="Apellidos" />
              <input 
                type="text" 
                v-model="formData.username" 
                placeholder="Nombre de Usuario (no use su nombre o apellidos reales)" 
                @blur="checkUsernameAvailability" 
                :disabled="loading.checkUsername"
              />
              <span class="error-message" v-if="errors.username">{{ errors.username }}</span>
              <div class="login-prompt-card" v-if="errors.username && errors.username.includes('ya está en uso')">
                <p>¿Ya tienes una cuenta? <a href="#" @click.prevent="redirectToSubscribePage" class="login-link">Haz click aquí para iniciar sesión</a></p>
              </div>
              <input 
                type="text" 
                v-model="formData.nif" 
                placeholder="NIF"
                @blur="validateNIF(formData.nif)"
              />
              <span class="error-message" v-if="errors.nif">{{ errors.nif }}</span>
              <input type="text" v-model="formData.direccion" placeholder="Dirección" />
              <input type="text" v-model="formData.provincia" placeholder="Provincia" />
              <input type="text" v-model="formData.codigoPostal" placeholder="Código Postal" />
              <input 
                type="email" 
                v-model="formData.email" 
                placeholder="Email" 
                @blur="checkEmailAvailability"
                :disabled="loading.checkEmail"
              />
              <span class="error-message" v-if="errors.email">{{ errors.email }}</span>
              <div class="login-prompt-card" v-if="errors.email && errors.email.includes('ya está registrado')">
                <p>¿Ya tienes una cuenta? <a href="#" @click.prevent="redirectToSubscribePage" class="login-link">Haz click aquí para iniciar sesión</a></p>
              </div>
              <input type="password" v-model="formData.password" placeholder="Contraseña" />
              <input type="password" v-model="formData.passwordConfirm" placeholder="Confirmar Contraseña" />
              <span class="error-message" v-if="errors.passwordConfirm">{{ errors.passwordConfirm }}</span>
              <span class="error-message" v-if="errors.stage2">{{ errors.stage2 }}</span>
            </div>
            <div class="navigation-buttons">
              <button class="secondary-btn" @click="previousStage">Atrás</button>
              <button type="submit" class="primary-btn" :disabled="loading.submitForm || Boolean(errors.username) || Boolean(errors.email)">
                {{ loading.submitForm ? 'Procesando...' : 'Siguiente' }}
              </button>
            </div>
          </form>
        </div>
        
        <!-- Stage 3: Completado -->
        <div v-if="currentStage === 3" class="stage stage-3">
          <h2>{{ errors.stage3 ? 'Ha ocurrido un error' : '¡Completado!' }}</h2>
          <div class="success-container" :class="{ 'error-container': errors.stage3 }">
            <div class="success-icon" v-if="!errors.stage3">
              <i class="material-icons">check_circle</i>
            </div>
            <div class="error-icon" v-if="errors.stage3">
              <i class="material-icons">error</i>
            </div>
            <p v-if="!errors.stage3">Te has suscrito al informe correctamente. El primer cobro puede tardar unos días.</p>
            <p v-if="errors.stage3">{{ errors.stage3 }}</p>
          </div>
          <div class="navigation-buttons centered">
            <button class="primary-btn highlight-btn" @click="resetFlow">
              {{ errors.stage3 ? 'Volver a intentar' : 'Ir al informe' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import { twoFactorRepository } from '@/features/account/api/twoFactorRepository';
  import SpanishIbanInput from '@/shared/components/common/SpanishIbanInput.vue';

  export default {
    name: 'InformeFlow',
    components: {
      SpanishIbanInput
    },
    data() {
      return {
        currentStage: 1,
        formData: {
          name: '',
          apellidos: '',
          username: '',
          nif: '',
          password: '',
          email: '',
          phone: '',
          phoneCode: '',
          iban: '',
          passwordConfirm: '',
          termsAccepted: false,
          direccion: '',
          provincia: '',
          codigoPostal: '',
        },
        validations: {
          nifValid: false,
          phoneVerified: false,
          passwordConfirm: '',
        },
        errors: {
          nif: '',
          username: '',
          email: '',
          stage2: '',
          phoneCode: '',
          iban: '',
          passwordConfirm: '',
          terms: '',
          stage3: '',
          checkUsername: false
        },
        infoMessages: {
          phoneCode: ''
        },
        loading: {
          sendPhoneCode: false,
          validatePhoneCode: false,
          submitForm: false,
          checkUsername: false,
          checkEmail: false
        },
        codeSent: false,
        changingPhone: false
      }
    },
    methods: {
      validateIbanChecksum(iban) {
        // Spanish IBAN must be 24 characters starting with ES
        if (!iban || iban.length !== 24 || !iban.startsWith('ES')) {
          return false;
        }

        // Move first 4 characters to the end
        const rearranged = iban.slice(4) + iban.slice(0, 4);
        
        // Replace letters with numbers (A=10, B=11, ..., Z=35)
        let numericString = '';
        for (let char of rearranged) {
          if (char >= '0' && char <= '9') {
            numericString += char;
          } else {
            numericString += (char.charCodeAt(0) - 55).toString();
          }
        }
        
        // Calculate mod 97
        let remainder = 0;
        for (let i = 0; i < numericString.length; i++) {
          remainder = (remainder * 10 + parseInt(numericString[i])) % 97;
        }
        
        return remainder === 1;
      },

      validateIBAN() {
        this.errors.iban = '';

        if (!this.formData.iban || this.formData.iban.length < 24) {
          this.errors.iban = 'El IBAN debe contener ES seguido de 22 dígitos';
          return false;
        }

        if (this.formData.iban.length !== 24 || !this.formData.iban.startsWith('ES')) {
          this.errors.iban = 'Formato de IBAN español inválido (ES + 22 dígitos)';
          return false;
        }

        if (!this.validateIbanChecksum(this.formData.iban)) {
          this.errors.iban = 'El IBAN introducido no es válido. Por favor, verifica los dígitos de control';
          return false;
        }

        return true;
      },

      validateNIF(nif) {
        const nifRegex = /^[0-9]{8}[TRWAGMYFPDXBNJZSQVHLCKE]$/i;
        const validChars = 'TRWAGMYFPDXBNJZSQVHLCKE';
        
        this.errors.nif = '';
        this.validations.nifValid = false;

        if (!nif) {
          this.errors.nif = 'El NIF es obligatorio';
          return false;
        }

        nif = nif.toUpperCase();
        
        if (!nifRegex.test(nif)) {
          this.errors.nif = 'Formato de NIF inválido';
          this.validations.nifValid = false;
          return false;
        }
        
        const number = nif.substr(0, 8);
        const letter = nif.substr(8, 1);
        const calculatedLetter = validChars[number % 23];
        
        if (letter !== calculatedLetter) {
          this.errors.nif = 'Letra del NIF incorrecta';
          this.validations.nifValid = false;
          return false;
        }
        
        this.errors.nif = '';
        this.validations.nifValid = true;
        return true;
      },

      async checkUsernameAvailability() {
        const username = this.formData.username.trim();
        this.errors.username = '';

        if (!username) {
          return;
        }

        if (username.length < 3 || username.length > 60) {
            this.errors.username = 'El nombre de usuario debe tener entre 3 y 60 caracteres.';
            return;
        }
        if (!/^[a-zA-Z0-9_]+$/.test(username)) {
            this.errors.username = 'El nombre de usuario solo puede contener letras, números y guiones bajos.';
            return;
        }

        this.loading.checkUsername = true;
        try {
          const response = await fetch(`/api/informe/check-username?username=${encodeURIComponent(username)}`);
          if (!response.ok) {
            throw new Error('Error al verificar el nombre de usuario.');
          }
          const data = await response.json();
          if (data.error) {
            this.errors.username = data.error;
          } else if (data.available === false) {
            this.errors.username = 'Este nombre de usuario ya está en uso. Por favor, elige otro.';
          } else {
            this.errors.username = '';
          }
        } catch (error) {
          console.error('Error checking username:', error);
          this.errors.username = error.message || 'No se pudo verificar el nombre de usuario en este momento.';
        } finally {
          this.loading.checkUsername = false;
        }
      },

      async checkEmailAvailability() {
        const email = this.formData.email.trim();
        this.errors.email = '';

        if (!email) {
          return;
        }

        // Simple email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(email)) {
          this.errors.email = 'Por favor, introduce un email válido.';
          return;
        }

        this.loading.checkEmail = true;
        try {
          const response = await fetch(`/api/informe/check-email?email=${encodeURIComponent(email)}`);
          if (!response.ok) {
            throw new Error('Error al verificar el email.');
          }
          const data = await response.json();
          if (data.error) {
            this.errors.email = data.error;
          } else if (data.available === false) {
            this.errors.email = 'Este email ya está registrado. Por favor, utiliza otro.';
          } else {
            this.errors.email = '';
          }
        } catch (error) {
          console.error('Error checking email:', error);
          this.errors.email = error.message || 'No se pudo verificar el email en este momento.';
        } finally {
          this.loading.checkEmail = false;
        }
      },

      async nextStage() {
        if (this.currentStage === 1) {
          this.errors.terms = '';
          this.errors.iban = '';

          if (!this.validations.phoneVerified) {
            return;
          }
          
          // Validate IBAN
          if (!this.validateIBAN()) {
            return;
          }

          if (!this.formData.termsAccepted) {
            this.errors.terms = 'Debe aceptar los términos y condiciones para continuar';
            return; 
          }
          
          this.currentStage++;
          return;
        }
        
        if (this.currentStage === 2) {
          this.errors.stage2 = '';
          this.errors.passwordConfirm = '';
          this.errors.nif = '';

          if (!this.formData.name || !this.formData.apellidos || !this.formData.username || !this.formData.nif || 
              !this.formData.password || !this.formData.passwordConfirm || 
              !this.formData.direccion || !this.formData.provincia || !this.formData.codigoPostal || !this.formData.email) {
            this.errors.stage2 = 'Todos los campos son obligatorios';
            return;
          }
          
          if (this.formData.password !== this.formData.passwordConfirm) {
            this.errors.passwordConfirm = 'Las contraseñas no coinciden';
            return;
          } else {
            this.errors.passwordConfirm = ''; // Clear error if they match
          }

          if (this.errors.username || this.errors.email) {
            return; // Don't proceed if there are username or email errors
          }

          if (!this.validateNIF(this.formData.nif)) return;
          
          try {
            this.loading.submitForm = true;
            const response = await fetch('/api/informe/create-user', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                full_name: `${this.formData.name} ${this.formData.apellidos}`.trim(),
                email: this.formData.email,
                phone: this.formData.phone,
                phone_code: this.formData.phoneCode,
                nif: this.formData.nif,
                password: this.formData.password,
                username: this.formData.username,
                iban: this.formData.iban,
                direccion: `${this.formData.direccion}, ${this.formData.provincia}, ${this.formData.codigoPostal}`.replace(/,\s*,/g, ',').replace(/^,\s*|,\s*$/g, '')
              })
            });

            let isSuccess = true;
            let errorMessage = '';

            if (!response.ok) {
              const errorText = await response.text();
              errorMessage = 'Error al crear el usuario';
              try {
                const errorData = JSON.parse(errorText);
                if (errorData.error) errorMessage = errorData.error;
              } catch (e) {
                // If parsing fails, use the error text directly if it exists
                if (errorText) errorMessage = errorText;
              }
              isSuccess = false;
            } else {
              const data = await response.json();
              if (!data.success) {
                errorMessage = data.error || 'Ha ocurrido un error al procesar la solicitud';
                isSuccess = false;
              }
            }

            this.currentStage = 3;
            
            if (!isSuccess) {
              this.errors.stage3 = errorMessage || 'Error al crear el usuario. Por favor, inténtelo de nuevo o contacte con soporte.';
            }
            
            return; // Return early to avoid incrementing stage again
          } catch (error) {
            console.error('Error:', error);
            this.currentStage = 3;
            this.errors.stage3 = error.message || 'Error al crear el usuario. Por favor, inténtelo de nuevo o contacte con soporte.';
            return;
          } finally {
            this.loading.submitForm = false;
          }
        }
      },

      async sendPhoneCode() {
        try {
          this.loading.sendPhoneCode = true;
          this.errors.phoneCode = ''; // Clear previous errors
          this.infoMessages.phoneCode = ''; // Clear previous info
          
          // Reset verification if changing phone
          if (this.changingPhone) {
            this.validations.phoneVerified = false;
            this.formData.phoneCode = '';
            this.changingPhone = false;
          }
          
          const response = await fetch('/api/validate-identity/generate-sms', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              phone: this.formData.phone,
              fresh: 'true'
            })
          });
          if (!response.ok) {
            const data = await response.json();
            throw new Error(data.error || 'Error al enviar el código');
          }
          this.infoMessages.phoneCode = 'Código enviado. Por favor, revise su teléfono.';
          this.codeSent = true;
        } catch (error) {
          console.error('Error:', error);
          this.errors.phoneCode = error.message;
          this.infoMessages.phoneCode = ''; // Clear info message on error
        } finally {
          this.loading.sendPhoneCode = false;
        }
      },

      async validatePhoneCode() {
        try {
          this.loading.validatePhoneCode = true;
          this.infoMessages.phoneCode = ''; // Clear info message before validation
          const response = await fetch('/api/validate-identity/validate-sms', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/x-www-form-urlencoded',
            },
            body: new URLSearchParams({
              code: this.formData.phoneCode
            })
          });
          
          if (!response.ok) {
            throw new Error('Error de comunicación con el servidor');
          }
          
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.error || 'Código inválido');
          }
          
          this.validations.phoneVerified = true;
          this.errors.phoneCode = '';
        } catch (error) {
          this.errors.phoneCode = error.message || 'Código inválido';
          this.validations.phoneVerified = false;
          this.infoMessages.phoneCode = ''; // Clear info message on error
          console.error('Error:', error);
        } finally {
          this.loading.validatePhoneCode = false;
        }
      },

      previousStage() {
        if (this.currentStage > 1) {
          this.currentStage--;
        }
      },
      resetFlow() {
        if (this.errors.stage3) {
          // Reset to stage 1 when there's an error
          this.currentStage = 1;
          // Reset form data
          this.formData = {
            name: '',
            apellidos: '',
            username: '',
            nif: '',
            password: '',
            email: '',
            phone: '',
            phoneCode: '',
            iban: '',
            passwordConfirm: '',
            termsAccepted: false,
            direccion: '',
            provincia: '',
            codigoPostal: '',
          };
          // Reset validations and errors
          this.validations.nifValid = false;
          this.validations.phoneVerified = false;
          this.errors = {
            nif: '',
            username: '',
            stage2: '',
            phoneCode: '',
            iban: '',
            passwordConfirm: '',
            terms: '',
            stage3: '',
            checkUsername: false
          };
          this.infoMessages.phoneCode = ''; // Reset info message
          this.codeSent = false;
          this.changingPhone = false;
        } else {
          // Instead of redirecting to login, automatically log in the user
          this.autoLogin();
        }
      },
      async autoLogin() {
        try {
          // First login with username and password
          const loginData = await twoFactorRepository.post('login', { user: this.formData.username, pass: this.formData.password });
          
          // If login successful but needs 2FA, we can't automate that part
          // so redirect to login page for manual 2FA
          if (loginData.needs_2fa) {
            window.location.href = process.env.VUE_APP_API + '/app/login';
            return;
          }
          
          // If login successful without 2FA, redirect to informes
          if (loginData.successful_login) {
            window.location.href = process.env.VUE_APP_API + '/app/informes';
          } else {
            // If login fails, redirect to login page for manual login
            window.location.href = process.env.VUE_APP_API + '/app/login';
          }
        } catch (error) {
          console.error('Auto-login error:', error);
          // In case of any error, fall back to manual login
          window.location.href = process.env.VUE_APP_API + '/app/login';
        }
      },
      enablePhoneChange() {
        this.changingPhone = true;
        this.validations.phoneVerified = false;
        this.formData.phoneCode = '';
        this.errors.phoneCode = '';
        this.infoMessages.phoneCode = ''; // Clear info message
        this.codeSent = false;
      },
      redirectToSubscribePage() {
        window.location.href = '/app/informe/subscribe';
      }
    }
  }
  </script>
  
  <style lang="scss" scoped>
  .payment-container {
    display: flex;
    min-height: 100vh;
    align-items: center;
    justify-content: center;
    padding: 20px 0;
  }
  
  .payment-flow {
    max-width: 600px;
    width: 100%;
    padding: 25px;
    background-color: var(--color-surface);
    border-radius: 10px;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    color: var(--color-text-primary);
  }
  
  h2 {
    font-size: 20px;
    font-weight: 600;
    margin-bottom: 25px;
    text-align: center;
    color: var(--color-text-primary);
  }
  
  .progress-indicator {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 30px;
    position: relative;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
    
    &:before {
      content: '';
      position: absolute;
      top: 50%;
      left: 0;
      right: 0;
      height: 2px;
      background-color: var(--color-muted);
      z-index: 1;
      transform: translateY(-50%);
    }
  }
  
  .circle {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background-color: var(--color-muted);
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: bold;
    position: relative;
    z-index: 2;
    transition: all 0.3s ease;
    margin-top: 0 !important;
    
    &.active {
      background-color: var(--primary);
      box-shadow: 0 0 0 4px rgba(0, 174, 221, 0.2);
      transform: scale(1.1);
    }
    
    &.completed {
      background-color: var(--success);
    }
  }
  
  .stage {
    display: flex;
    flex-direction: column;
    gap: 15px;
    transition: all 0.3s ease;
  }
  
  .form-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: var(--color-background);
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 10px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  input {
    padding: 12px 15px;
    border: 1px solid var(--color-outline);
    border-radius: 8px;
    font-size: 14px;
    background-color: var(--color-background);
    color: var(--color-text-primary);
    transition: border-color 0.3s;
    
    &:focus {
      border-color: var(--primary);
      outline: none;
      box-shadow: 0 0 0 3px rgba(0, 174, 221, 0.1);
    }
    
    &::placeholder {
      color: var(--color-text-inactive);
    }
  }
  
  button {
    padding: 12px 15px;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-weight: 500;
    transition: all 0.2s ease;
    
    &.primary-btn {
      background-color: var(--primary);
      color: var(--color-text-on-highlight);
      
      &:hover:not(:disabled) {
        background-color: #0098c4;
      }
    }
    
    &.secondary-btn {
      background-color: var(--color-active-surface);
      color: var(--color-text-primary);
      
      &:hover:not(:disabled) {
        background-color: #223a4a;
      }
    }
    
    &:disabled {
      background-color: var(--color-muted);
      cursor: not-allowed;
      opacity: 0.6;
    }
  }
  
  .verification-group {
    display: flex;
    flex-direction: column;
    gap: 12px;
    background-color: var(--color-background);
    padding: 20px;
    border-radius: 8px;
    margin-bottom: 20px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  }
  
  .navigation-buttons {
    display: flex;
    justify-content: space-between;
    margin-top: 10px;
    
    &.centered {
      justify-content: center;
    }
    
    button {
      min-width: 120px;
    }
  }
  
  .error-message {
    color: var(--error);
    font-size: 13px;
    font-weight: 500;
    margin-top: 5px;
  }
  
  .success-message {
    color: var(--success);
    font-size: 13px;
    font-weight: 500;
    margin-top: 5px;
  }
  
  .success-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    background-color: rgba(67, 160, 71, 0.1);
    padding: 30px;
    border-radius: 8px;
    margin: 20px 0;
    
    .success-icon {
      font-size: 64px;
      color: var(--success);
      margin-bottom: 20px;
      
      i {
        font-size: 64px;
      }
    }
    
    p {
      font-size: 16px;
      text-align: center;
      line-height: 1.5;
    }
  }
  
  @media (max-width: 767px) {
    .payment-flow {
      margin: 20px;
      padding: 20px;
    }
    
    .navigation-buttons button {
      min-width: 100px;
    }
  }
  
  .main-title {
    font-size: 24px;
    font-weight: 700;
    text-align: center;
    margin-bottom: 25px;
    color: var(--primary);
  }
  
  .terms-container {
    margin-top: 15px;
  }
  
  .checkbox-container {
    display: flex;
    align-items: flex-start;
    gap: 10px;
  }
  
  .terms-text {
    font-size: 14px;
    font-weight: 500;
    color: var(--color-text-primary);
  }
  
  .info-container {
    background-color: rgba(0, 174, 221, 0.1);
    border-radius: 8px;
    padding: 15px;
    margin: 10px 0;
  }
  
  .info-text {
    font-size: 13px;
    line-height: 1.5;
    color: var(--color-text-secondary);
    margin: 0;
  }
  
  .highlight-btn {
    font-size: 18px;
    font-weight: 600;
    padding: 15px 30px;
    min-width: 200px;
    transition: all 0.3s ease;
    box-shadow: 0 4px 10px rgba(0, 174, 221, 0.3);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 12px rgba(0, 174, 221, 0.4);
    }
  }
  
  .error-container {
    background-color: rgba(244, 67, 54, 0.1) !important;
    
    .error-icon {
      font-size: 64px;
      color: var(--error);
      margin-bottom: 20px;
      
      i {
        font-size: 64px;
      }
    }
  }
  
  .login-link {
    color: var(--color-highlight);
    text-decoration: underline;
    font-weight: 500;
    cursor: pointer;
    
    &:hover {
      color: #0098c4;
    }
  }
  
  .phone-input-container {
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    margin-bottom: 15px;
    
    input {
      flex: 1;
    }
    
    button.secondary-btn {
      white-space: nowrap;
    }
  }
  
  .code-verification-container {
    display: flex;
    flex-direction: column;
    gap: 12px;
    border-top: 1px solid var(--color-outline);
    padding-top: 15px;
    margin-top: 5px;
  }
  
  .text-btn {
    background: none;
    border: none;
    padding: 5px 10px;
    font: inherit;
    cursor: pointer;
    outline: inherit;
    color: var(--color-highlight);
    text-decoration: underline;
    font-weight: 500;
    font-size: 14px;
    text-align: left;
    
    &:hover {
      color: #0098c4;
    }
  }
  
  .input-disabled {
    background-color: var(--color-background);
    color: var(--color-text-inactive);
    border: 1px solid var(--color-outline);
    opacity: 0.6;
    cursor: not-allowed;
  }
  
  .info-message {
    color: var(--success);
    font-size: 13px;
    font-weight: 500;
    margin-top: 5px;
  }

  .input-active-step {
    background-color: var(--color-active-surface) !important; /* Use !important to ensure override if needed */
    border-color: var(--primary) !important; /* Use !important */
    box-shadow: 0 0 0 3px rgba(0, 174, 221, 0.1) !important; /* Use !important */
  }

  .login-prompt-card {
    background-color: var(--color-active-surface);
    padding: 10px 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    text-align: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    font-size: 14px;
  }

  .login-prompt-card p {
    margin: 0;
    color: var(--color-text-secondary);
  }

  .login-prompt-card .login-link {
    color: var(--primary);
    font-weight: 600;
    text-decoration: none;
  }

  .login-prompt-card .login-link:hover {
    text-decoration: underline;
  }
  </style>
